/**
 * MergeDepartmentModal.tsx
 * Merges one department master into another: repoints every project org unit
 * from the source to the target and deactivates the source. Conflicts (projects
 * that have BOTH departments active) are surfaced via a persistent toast.
 */

import { useMemo, useState } from 'react';
import { Alert, Modal, Select } from 'antd';
import { toast } from 'react-toastify';

import { masterId } from '../../../services/epcDepartmentMasterService';
import { useDepartmentMasters, type EpcDepartmentMaster } from './useDepartmentMasters';

interface MergeDepartmentModalProps {
  source: EpcDepartmentMaster;
  candidates: EpcDepartmentMaster[];
  onClose: () => void;
}

export default function MergeDepartmentModal({ source, candidates, onClose }: MergeDepartmentModalProps) {
  const { mergeMutation } = useDepartmentMasters();
  const [targetId, setTargetId] = useState<string | null>(null);

  const sourceId = masterId(source);

  const options = useMemo(
    () =>
      candidates
        .filter((c) => masterId(c) !== sourceId && !c.inactive)
        .map((c) => ({
          value: masterId(c),
          label: `${c.name} · used in ${c.usageCount ?? 0} project${(c.usageCount ?? 0) === 1 ? '' : 's'}`,
        })),
    [candidates, sourceId],
  );

  const handleMerge = async () => {
    if (!targetId) return;
    try {
      const { repointedOrgUnits, conflicts } = await mergeMutation.mutateAsync({ sourceId, targetId });
      toast.success(`Merged — ${repointedOrgUnits} org unit${repointedOrgUnits === 1 ? '' : 's'} repointed`);
      if (conflicts.length > 0) {
        // Persistent so the operator doesn't lose the follow-up work list.
        toast.warning(
          `${conflicts.length} project(s) have both departments active — resolve manually in Team Hierarchy`,
          { autoClose: false },
        );
      }
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      toast.error(message || 'Merge failed');
    }
  };

  return (
    <Modal
      open
      title={`Merge "${source.name}" into…`}
      okText="Merge"
      okButtonProps={{ danger: true, disabled: !targetId }}
      confirmLoading={mergeMutation.isPending}
      cancelButtonProps={{ disabled: mergeMutation.isPending }}
      onOk={handleMerge}
      onCancel={onClose}
      destroyOnHidden
    >
      <div className="space-y-4 pt-2">
        <Alert
          type="warning"
          showIcon
          message={
            <>
              Repoints every project&apos;s org unit from &lsquo;{source.name}&rsquo; to the target and deactivates
              &lsquo;{source.name}&rsquo;. Schedules and teams follow automatically.
            </>
          }
        />
        <div>
          <label htmlFor="merge-target-select" className="block text-xs font-semibold mb-1.5 text-slate-600">
            Merge into
          </label>
          <Select
            id="merge-target-select"
            className="w-full"
            placeholder="Select the target department"
            value={targetId ?? undefined}
            onChange={(v) => setTargetId(v)}
            options={options}
            showSearch
            optionFilterProp="label"
          />
        </div>
      </div>
    </Modal>
  );
}
